import useProjectId from '../../customHooks/useProjectId'
import { useQuery } from '@tanstack/react-query'
import { useAccessToken } from '../useAccessToken'
import { ProjectInfo } from '../../../../interfaces/project/ProjectInfo'
import { useProjektprognosInteractionContext } from '../../context/ProjektprognosInteractionContext'
import { useCallback, useEffect } from 'react'
import { makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'
import { useHandleRetry } from '../useHandleRetry'

const useFetch = () => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()

  const queryFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<ProjectInfo>(
        `/Projects/${projectId}`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        return data
      })

      return fetchedData
    })
  }, [getAccessToken, projectId])

  return { projectId, queryFunction }
}

interface ProjektprognosProjectInfoOptions {
  enabled?: boolean
}

export const useProjektprognosProjectInfo = ({
  enabled = true,
}: ProjektprognosProjectInfoOptions = {}) => {
  const { projectId, queryFunction } = useFetch()
  const handleRetry = useHandleRetry()
  const { loadProjektprognos, setLoadProjektprognos } =
    useProjektprognosInteractionContext()

  const query = useQuery({
    queryKey: ['projectInfo', projectId],
    queryFn: queryFunction,
    enabled: enabled && !!projectId && !!queryFunction,
    retry: handleRetry,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
  })

  useEffect(() => {
    if (query.data && loadProjektprognos) {
      setLoadProjektprognos(false)
    }
  }, [query.data, loadProjektprognos, setLoadProjektprognos])

  return query
}
